import dayjs from "dayjs";
import { Archive, UserX } from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { ForeignNationalsAPI } from "@/api/foreignNationals";
import { Button } from "@/components/ui/button";

import { TooltipProvider } from "@/components/ui/tooltip";
import { useRequestsIde } from "@/features/requestsIde/context/RequestsIdeContext";
import {
  RequestsCenter,
  RequestsExplorer,
  RequestsWorkspace
} from "@/features/requestsIde/RequestsIde";
import { RequestsQuickAction } from "@/features/requestsIde/explorer/RequestsQuickAction";
import { useUserProfiles } from "@/features/userProfiles/context/UserProfilesContext";
import { cn } from "@/lib/utils";

import { ClientNav, type ClientNavIdentity } from "./components/ClientNav";
import { PanelEmpty, PanelError } from "./components/PanelState";
import {
  ClientProfileProvider,
  useClientProfile
} from "./context/ClientProfileContext";
import { ClientHeader } from "./header/ClientHeader";
import { displayName, initialsOf } from "./header/clientHeaderModel";
import { usePortalState } from "./hooks/usePortalState";
import { CreateCaseSheet } from "./tabs/cases/CreateCaseSheet";
import { ClientTabPanel } from "./tabs/ClientTabPanel";
import { DeleteClientDialog } from "./tabs/settings/DeleteClientDialog";

const ClientProfileLayout: FC<{ clientName: string }> = ({ clientName }) => {
  const {
    clientId,
    client,
    isLoadingClient,
    clientError,
    reloadClient,
    activeTab,
    nav,
    url,
    badges,
    matters,
    createCase,
    deleteClient
  } = useClientProfile();
  const navigate = useNavigate();
  const [restoring, setRestoring] = useState(false);
  const { get: getProfile, ensure: ensureProfiles } = useUserProfiles();
  const requestsActive = activeTab === "outgoing_intakes";
  // The Requests list is a card like the other list sections; an open request
  // (the editor, a review) is a full page and keeps its own layout.
  const { activeArtifact } = useRequestsIde();

  const portal = usePortalState(clientId, Boolean(client?.email));
  const newestVisa =
    (matters.newestMatter?.case?.visa ?? "").toString().trim() || null;

  useEffect(() => {
    ensureProfiles([clientId]);
  }, [clientId, ensureProfiles]);
  const identity = useMemo<ClientNavIdentity | undefined>(
    () =>
      client
        ? {
            name: clientName || "Unknown Client",
            initials: initialsOf(clientName, "CB"),
            avatarUrl: getProfile(clientId)?.avatar_data_url ?? null,
            email: client.email ?? null,
            visa: newestVisa,
            portal
          }
        : undefined,
    [client, clientId, clientName, getProfile, newestVisa, portal]
  );
  const totals = useMemo(
    () => ({
      cases: matters.matters.length,
      outgoing_intakes: badges.intakesCount ?? 0
    }),
    [badges.intakesCount, matters.matters.length]
  );

  useEffect(() => {
    if (!clientError || client) return undefined;
    let live = true;
    ForeignNationalsAPI.getMergedInto(clientId).then((merged) => {
      if (!live || !merged) return;
      toast.info(`This record was merged into ${merged.name}`);
      navigate(`/clients/${merged.merged_into_id}`, { replace: true });
    });
    return () => {
      live = false;
    };
  }, [client, clientError, clientId, navigate]);

  const restore = async () => {
    setRestoring(true);
    try {
      await ForeignNationalsAPI.setArchived(clientId, false);
      await reloadClient();
      toast.success("Client restored");
    } catch (e) {
      toast.error("Couldn't restore", {
        description: e instanceof Error ? e.message : undefined
      });
    } finally {
      setRestoring(false);
    }
  };

  const clientEmail = client?.email ?? null;

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-surface">
      <ClientHeader />
      <div className="flex min-h-0 w-full flex-1 items-stretch overflow-hidden">
        <div
          className={cn(
            "h-full min-h-0 flex-none overflow-hidden transition-[width,opacity] duration-200 ease-out",
            nav.isOpen
              ? "w-68 opacity-100"
              : "pointer-events-none w-0 opacity-0"
          )}
          aria-hidden={!nav.isOpen}
        >
          <ClientNav
            activeTab={activeTab}
            onSelect={(tab) =>
              tab === "outgoing_intakes"
                ? navigate(url.intakesBaseHref)
                : url.setTab(tab)
            }
            badgeCounts={badges.counts}
            totals={totals}
            isProfileExpanded={nav.isProfileExpanded}
            onProfileExpandedChange={nav.setProfileExpanded}
            identity={identity}
            requestsTree={<RequestsExplorer />}
            requestsQuickAction={<RequestsQuickAction />}
            isRequestsExpanded={nav.isRequestsExpanded}
            onRequestsExpandedChange={nav.setRequestsExpanded}
          />
        </div>

        <div
          className="client-page flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
          data-client-tab={activeTab}
        >
          {client?.archived ? (
            <div
              className="flex items-center gap-2 border-b border-line-2 bg-fill-1 px-4 py-2 text-base"
              data-testid="client-archived-banner"
            >
              <Archive className="size-4 shrink-0 text-fg-3" />
              <span className="min-w-0 flex-1">
                Archived
                {client.archived_at
                  ? ` on ${dayjs(client.archived_at).format("MMM D, YYYY")}`
                  : ""}
                . Hidden from lists, and reminders are paused.
              </span>
              <Button
                size="xs"
                variant="secondary"
                disabled={restoring}
                onClick={() => void restore()}
              >
                Restore
              </Button>
            </div>
          ) : null}
          {clientError && !isLoadingClient && !client ? (
            <PanelError
              title="Something Went Wrong"
              message="We weren't able to load this client. Please try again. If the problem persists, please contact support@caseblink.com."
              onRetry={() => reloadClient().catch(() => undefined)}
            />
          ) : requestsActive ? (
            <div
              className="client-tab contents"
              data-layout={activeArtifact ? "page" : "card"}
              data-tab="outgoing_intakes"
            >
              <RequestsCenter
                clientName={clientName}
                clientEmail={clientEmail}
              />
            </div>
          ) : (
            <ClientTabPanel />
          )}
        </div>
      </div>

      <CreateCaseSheet
        open={createCase.isOpen}
        onOpenChange={createCase.setOpen}
        clientId={clientId}
        initialMatter={createCase.prefillMatter}
        onCreated={createCase.onCreated}
      />
      <DeleteClientDialog
        open={deleteClient.isOpen}
        onOpenChange={deleteClient.setOpen}
        clientId={clientId}
        firstName={client?.first_name ?? ""}
        lastName={client?.last_name ?? ""}
      />
    </div>
  );
};

/** The Requests state lives above the whole profile: nav, center and Casey's panel. */
const ClientWorkspace: FC = () => {
  const { clientId, client, whitelabel } = useClientProfile();
  const clientName = client
    ? displayName([client.first_name, client.middle_name, client.last_name])
    : "";
  return (
    <RequestsWorkspace
      foreignNationalId={clientId}
      clientName={clientName}
      clientEmail={client?.email ?? null}
      lawFirmName={whitelabel.lawFirmName}
      lawFirmLogoUrl={whitelabel.lawFirmLogoUrl}
      enableWhitelabelForeignNationalIntake={
        whitelabel.enableWhitelabelForeignNationalIntake
      }
    >
      <ClientProfileLayout clientName={clientName} />
    </RequestsWorkspace>
  );
};

/** `/clients/:clientId` — the client profile, with its own header like the case IDE. */
export const ClientProfilePage: FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();

  if (!clientId) {
    return (
      <PanelEmpty
        title="Client Not Found"
        description="We couldn't determine which client to load."
        icon={UserX}
        action={{
          label: "Back to Clients",
          onClick: () => navigate("/clients")
        }}
      />
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <ClientProfileProvider clientId={clientId}>
        <ClientWorkspace />
      </ClientProfileProvider>
    </TooltipProvider>
  );
};
